/**
 * Verbose Device Communication Logger
 * 
 * Provides detailed logging of all device communication for diagnostics
 */
import { isLogLevelEnabled } from '@/lib/logger';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'trace';

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  context: string;
  message: string;
  data?: unknown;
}

// Logged `data` can carry decoded DevMessage fields (e.g. State.processingId) as native
// bigint, which JSON.stringify cannot serialize on its own.
function bigintSafeReplacer(_key: string, value: unknown): unknown {
  return typeof value === 'bigint' ? value.toString() : value;
}

class DeviceLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 500; // Keep last 500 logs in memory
  private isEnabled = true;
  private enableTrace = isLogLevelEnabled('trace');

  /**
   * Setup verbose logging - call this early in app startup
   */
  public setup() {
    // Make logger available in chrome console
    if (typeof window !== 'undefined') {
      (window as any).__deviceLogs = {
        getLogs: () => this.formatLogs(),
        clearLogs: () => this.logs = [],
        exportLogs: () => JSON.stringify(this.logs, bigintSafeReplacer, 2),
        enableTrace: (enabled: boolean) => { this.enableTrace = enabled; },
      };

      if (isLogLevelEnabled('info')) {
        console.info(
          '%c[Device Logger] Active. Use window.__deviceLogs to access logs.',
          'color: #0066cc; font-weight: bold;'
        );
      }
    }
  }

  /**
   * Log a message at specified level
   */
  private logMessage(
    level: LogLevel,
    context: string,
    message: string,
    data?: unknown
  ) {
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      context,
      message,
      data,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Respect the global log level before writing to console.
    if (!isLogLevelEnabled(level)) return;

    // Also log to console with styling
    const timestamp = entry.timestamp.toISOString().split('T')[1];
    const colors: Record<LogLevel, string> = {
      trace: 'color: #999;',
      debug: 'color: #0066cc;',
      info: 'color: #009900;',
      warn: 'color: #ff6600;',
      error: 'color: #ff0000;',
    };

    const prefix = `[${timestamp}] [${context}]`;
    console.log(`%c${prefix}`, colors[level], message, data ? data : '');
  }

  debug(context: string, message: string, data?: unknown) {
    if (!this.isEnabled) return;
    this.logMessage('debug', context, message, data);
  }

  info(context: string, message: string, data?: unknown) {
    if (!this.isEnabled) return;
    this.logMessage('info', context, message, data);
  }

  warn(context: string, message: string, data?: unknown) {
    if (!this.isEnabled) return;
    this.logMessage('warn', context, message, data);
  }

  error(context: string, message: string, data?: unknown) {
    if (!this.isEnabled) return;
    this.logMessage('error', context, message, data);
  }

  trace(context: string, message: string, data?: unknown) {
    if (!this.isEnabled || !this.enableTrace) return;
    this.logMessage('trace', context, message, data);
  }

  /**
   * Format logs for console display
   */
  private formatLogs(): string {
    const grouped = this.logs.reduce(
      (acc, log) => {
        if (!acc[log.context]) acc[log.context] = [];
        acc[log.context].push(log);
        return acc;
      },
      {} as Record<string, LogEntry[]>
    );

    let output = '\n=== DEVICE COMMUNICATION LOG ===\n';
    output += `Total entries: ${this.logs.length}\n\n`;

    for (const [context, entries] of Object.entries(grouped)) {
      output += `\n--- ${context} (${entries.length} entries) ---\n`;
      for (const entry of entries) {
        const time = entry.timestamp.toISOString().split('T')[1];
        output += `[${time}] ${entry.level.toUpperCase()}: ${entry.message}`;
        if (entry.data) {
          output += ` | ${JSON.stringify(entry.data, bigintSafeReplacer)}`;
        }
        output += '\n';
      }
    }

    return output;
  }

  enable() {
    this.isEnabled = true;
  }

  disable() {
    this.isEnabled = false;
  }

  clear() {
    this.logs = [];
  }

  /**
   * Export logs for debugging
   */
  export(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Static setup method for convenience
   */
  public static setup(): DeviceLogger {
    const instance = new DeviceLogger();
    instance.setup();
    return instance;
  }
}

// Global instance
export const deviceLogger = new DeviceLogger();
export { DeviceLogger };
